import React, { useContext, useState, useCallback, useEffect } from 'react';
import { Nav } from 'react-bootstrap';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { openLogoutPrompt } from './logoutPrompt';
import '../Customize_Css/Sidebar.css';
import { MyContext } from './Context';

const Sidebar = () => {
  const { isSidebarOpen } = useContext(MyContext);
  const [vendorsOpen, setVendorsOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);

  const epicxTenantId = localStorage.getItem('epicxTenantId');
  const userRole = localStorage.getItem('role');

  const navigate = useNavigate();
  const location = useLocation();

  const isSubMenuActive = useCallback(
    (paths) => paths.some((path) => location.pathname.includes(path)),
    [location.pathname]
  );

  // Set active submenus on route change
  useEffect(() => {
    if (
      userRole === 'tenant' &&
      isSubMenuActive(['/view-orders', '/viewBanners', '/view-offer', '/view-promocode', '/priceeview', '/viewTickets'])
    ) {
      setSalesOpen(true);
    }
    if (
      userRole === 'tenant' &&
      isSubMenuActive([`/viewvendor/${epicxTenantId}`, '/view-delivery-vendor', '/view-delivery-vendor-agents', '/add-delivery-agent', '/update-delivery-agent', '/view-independent-delivery'])
    ) {
      setVendorsOpen(true);
    }
    if (
      userRole === 'tenant' &&
      isSubMenuActive(['/Add-product-image', '/viewProducts', '/viewStocks'])
    ) {
      setInventoryOpen(true);
    }
  }, [location.pathname, epicxTenantId, userRole, isSubMenuActive]);

  // Toggle functions
  const toggleSales = () => setSalesOpen((prev) => !prev);
  const toggleVendors = () => setVendorsOpen((prev) => !prev);
  const toggleInventory = () => setInventoryOpen((prev) => !prev);

  const onMenuKey = (handler) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handler();
    }
  };

  // Nav link styles
  const navClass = ({ isActive }) => `nav-link ${isActive ? 'active' : ''}`;
  const subNavClass = ({ isActive }) => `submenu-item nav-link ${isActive ? 'active' : ''}`;

  return (
    <>
      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-caption">Menu</div>
        <Nav className="flex-column sidebar-nav">
          {userRole === 'tenant' && (
            <>
              <NavLink to="/dashboard" className={navClass} title="Dashboard">
                <i className="bi bi-grid"></i>
                <span className="nav-label">Dashboard</span>
              </NavLink>

              <NavLink to="/viewCustomer" className={navClass} title="Customers">
                <i className="bi bi-person-check"></i>
                <span className="nav-label">Customers</span>
              </NavLink>

              <NavLink to="/chat" className={navClass} title="Chat">
                <i className="bi bi-chat-dots"></i>
                <span className="nav-label">Chat</span>
              </NavLink>

              <div
                className={`nav-link menu-toggle ${salesOpen ? 'menu-open' : ''} ${isSubMenuActive(['/view-orders', '/viewBanners', '/view-offer', '/view-promocode', '/priceeview', '/viewTickets']) ? 'section-active' : ''}`}
                onClick={toggleSales}
                onKeyDown={onMenuKey(toggleSales)}
                role="button"
                tabIndex={0}
                aria-expanded={salesOpen}
                title="Sales"
              >
                <i className="bi bi-cash-stack"></i>
                <span className="nav-label">Sales</span>
                <i className={`bi ${salesOpen ? 'bi-chevron-down' : 'bi-chevron-right'} submenu-icon`}></i>
              </div>
              {salesOpen && (
                <div className="submenu">
                  <NavLink
                    to="/view-orders"
                    title="Orders"
                    className={({ isActive }) =>
                      `submenu-item nav-link ${isActive ? 'active' : ''} ${!isActive && isSubMenuActive(['/viewTickets']) ? 'section-active' : ''}`
                    }
                  >
                    <i className="bi bi-card-list"></i>
                    <span className="nav-label">Orders</span>
                  </NavLink>
                  <div className="submenu nested-submenu">
                    <NavLink to="/viewTickets" className={subNavClass} title="Tickets">
                      <i className="bi bi-ticket"></i>
                      <span className="nav-label">Tickets</span>
                    </NavLink>
                  </div>
                  <NavLink to="/viewBanners" className={subNavClass} title="Banners"><i className="bi bi-image"></i><span className="nav-label">Banners</span></NavLink>
                  <NavLink to="/view-offer" className={subNavClass} title="Offers"><i className="bi bi-gift"></i><span className="nav-label">Offers</span></NavLink>
                  <NavLink to="/view-promocode" className={subNavClass} title="Promocode"><i className="bi bi-ticket-perforated"></i><span className="nav-label">Promocode</span></NavLink>
                  <NavLink to="/priceeview" className={subNavClass} title="PriceHeads"><i className="bi bi-tags"></i><span className="nav-label">PriceHeads</span></NavLink>
                </div>
              )}

              <div
                className={`nav-link menu-toggle ${vendorsOpen ? 'menu-open' : ''} ${isSubMenuActive([`/viewvendor/${epicxTenantId}`, '/view-delivery-vendor', '/view-delivery-vendor-agents', '/add-delivery-agent', '/update-delivery-agent', '/view-independent-delivery']) ? 'section-active' : ''}`}
                onClick={toggleVendors}
                onKeyDown={onMenuKey(toggleVendors)}
                role="button"
                tabIndex={0}
                aria-expanded={vendorsOpen}
                title="Vendors"
              >
                <i className="bi bi-people"></i>
                <span className="nav-label">Vendors</span>
                <i className={`bi ${vendorsOpen ? 'bi-chevron-down' : 'bi-chevron-right'} submenu-icon`}></i>
              </div>
              {vendorsOpen && (
                <div className="submenu">
                  <NavLink to={`/viewvendor/${epicxTenantId}`} className={subNavClass} title="Product Vendors"><i className="bi bi-shop"></i><span className="nav-label">Product Vendors</span></NavLink>
                  <NavLink
                    to="/view-delivery-vendor"
                    end
                    title="Delivery Vendors"
                    className={({ isActive }) =>
                      `submenu-item nav-link ${isActive ? 'active' : ''} ${!isActive && isSubMenuActive(['/view-delivery-vendor-agents', '/add-delivery-agent', '/update-delivery-agent']) ? 'section-active' : ''}`
                    }
                  >
                    <i className="bi bi-truck"></i>
                    <span className="nav-label">Delivery Vendors</span>
                  </NavLink>
                  <div className="submenu nested-submenu">
                    <NavLink to="/view-delivery-vendor-agents" className={subNavClass} title="Delivery Vendor Agents">
                      <i className="bi bi-person-badge"></i>
                      <span className="nav-label">Delivery Vendor Agents</span>
                    </NavLink>
                  </div>
                  <NavLink to="/view-independent-delivery" className={subNavClass} title="Independent Delivery"><i className="bi bi-person-lines-fill"></i><span className="nav-label">Independent Delivery</span></NavLink>
                </div>
              )}

              <div
                className={`nav-link menu-toggle ${inventoryOpen ? 'menu-open' : ''} ${isSubMenuActive(['/Add-product-image', '/viewProducts', '/viewStocks']) ? 'section-active' : ''}`}
                onClick={toggleInventory}
                onKeyDown={onMenuKey(toggleInventory)}
                role="button"
                tabIndex={0}
                aria-expanded={inventoryOpen}
                title="Inventory"
              >
                <i className="bi bi-box-seam"></i>
                <span className="nav-label">Inventory</span>
                <i className={`bi ${inventoryOpen ? 'bi-chevron-down' : 'bi-chevron-right'} submenu-icon`}></i>
              </div>
              {inventoryOpen && (
                <div className="submenu">
                  <NavLink to="/Add-product-image" className={subNavClass} title="Add Images"><i className="bi bi-images"></i><span className="nav-label">Add Images</span></NavLink>
                  <NavLink to="/viewProducts" className={subNavClass} title="Products"><i className="bi bi-bag"></i><span className="nav-label">Products</span></NavLink>
                  <NavLink to="/viewStocks" className={subNavClass} title="Stocks"><i className="bi bi-graph-up"></i><span className="nav-label">Stocks</span></NavLink>
                </div>
              )}

              <NavLink to="/customize-field" className={navClass} title="Customize Field">
                <i className="bi bi-sliders"></i>
                <span className="nav-label">Customize Field</span>
              </NavLink>

              <NavLink to="/view-employee" className={navClass} title="Employee">
                <i className="bi bi-person-workspace"></i>
                <span className="nav-label">Employee</span>
              </NavLink>
              <NavLink to="/settings" className={navClass} title="Settings">
                <i className="bi bi-gear"></i>
                <span className="nav-label">Settings</span>
              </NavLink>

              <button
                type="button"
                className="nav-link logout-link text-start"
                onClick={() => openLogoutPrompt(navigate)}
                title="Logout"
              >
                <i className="bi bi-box-arrow-right"></i>
                <span className="nav-label">Logout</span>
              </button>
            </>
          )}

          {userRole === 'employee' && (
            <>
              <NavLink to="/view-orders" className={navClass} title="Orders">
                <i className="bi bi-card-list"></i>
                <span className="nav-label">Orders</span>
              </NavLink>
              <NavLink to="/chat" className={navClass} title="Chat">
                <i className="bi bi-chat-dots"></i>
                <span className="nav-label">Chat</span>
              </NavLink>
              <button
                type="button"
                className="nav-link logout-link text-start"
                onClick={() => openLogoutPrompt(navigate)}
                title="Logout"
              >
                <i className="bi bi-box-arrow-right"></i>
                <span className="nav-label">Logout</span>
              </button>
            </>
          )}
        </Nav>
      </div>
    </>
  );
};

export default Sidebar;
